import React, { useMemo } from 'react';
import { parseChatBlocks } from '../../features/assistant/chatMarkdown';

type ChatMessageContentProps = {
  content: string;
  /** User bubbles are dark; tables and text pick contrasting borders from this. */
  tone: 'user' | 'assistant';
};

/**
 * Renders an assistant reply as text plus any pipe tables it contains, so a
 * KPI summary arrives as a real table instead of a wall of `|` characters.
 */
export const ChatMessageContent: React.FC<ChatMessageContentProps> = ({ content, tone }) => {
  const blocks = useMemo(() => parseChatBlocks(content), [content]);
  const border = tone === 'user' ? 'border-blue-400/60' : 'border-slate-200';
  const headBg = tone === 'user' ? 'bg-blue-500/40' : 'bg-slate-50';

  return (
    <div className="space-y-2">
      {blocks.map((block, index) =>
        block.kind === 'text' ? (
          <div key={index} className="whitespace-pre-wrap">
            {block.text}
          </div>
        ) : (
          <div key={index} className={`overflow-x-auto rounded-lg border ${border}`}>
            <table className="min-w-full border-collapse text-left text-xs">
              <thead className={headBg}>
                <tr>
                  {block.header.map((cell, col) => (
                    <th
                      key={col}
                      scope="col"
                      className={`whitespace-nowrap px-2.5 py-1.5 font-semibold ${
                        col > 0 ? 'text-right' : ''
                      }`}
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={`border-t ${border}`}>
                    {row.map((cell, col) => (
                      <td
                        key={col}
                        className={`px-2.5 py-1.5 align-top ${
                          col > 0 ? 'text-right tabular-nums whitespace-nowrap' : ''
                        }`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default ChatMessageContent;
